import { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import * as XLSX from "xlsx";
import api from "../services/api";
import { formatToDisplayDate } from "../services/dateFormatter";

const fmt  = n => "₹" + Math.round(n || 0).toLocaleString("en-IN");
const fmtN = n => Math.round(n || 0).toLocaleString("en-IN");

function computePeriodLabel(periodStart, contractStart, installment) {
  if (!periodStart) return null;
  try {
    const start = new Date(periodStart);
    const contract = new Date(contractStart || periodStart);
    if (isNaN(start) || isNaN(contract)) return null;
    const monthDiff = (start.getFullYear() - contract.getFullYear()) * 12 + (start.getMonth() - contract.getMonth());
    if (installment === "Monthly") {
      return start.toLocaleString('default', { month: 'short', year: 'numeric' });
    }
    if (installment === "Quarterly") {
      const index = Math.floor(monthDiff / 3) + 1;
      return `Q${index}`;
    }
    if (installment === "Half-Yearly") {
      const index = Math.floor(monthDiff / 6) + 1;
      const suffix = index === 1 ? 'st' : index === 2 ? 'nd' : index === 3 ? 'rd' : 'th';
      return `${index}${suffix} Half`;
    }
    return "Full";
  } catch (e) { return null; }
}

function getFinancialYear(dateValue) {
  if (!dateValue) return "Unknown";
  const dt = new Date(dateValue);
  if (isNaN(dt)) return "Unknown";
  const year = dt.getFullYear();
  return dt.getMonth() >= 3
    ? `${year}-${String(year + 1).slice(2)}`
    : `${year - 1}-${String(year).slice(2)}`;
}

function PrivilegeNotice({ role }) {
  const isAdmin = role === "Admin";
  return (
    <div className={`privilege-notice ${isAdmin ? "privilege-admin" : "privilege-acct"}`}>
      <svg width="16" height="16" fill="none" stroke={isAdmin?"#22d3ee":"#2dd4bf"} strokeWidth="2" viewBox="0 0 24 24" style={{flexShrink:0}}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
      </svg>
      {isAdmin
        ? <span>Operating with <strong>Admin privileges</strong>. Full control authorized.</span>
        : <span>Operating with <strong>Accountant privileges</strong>. Invoice operations allowed.</span>
      }
    </div>
  );
}

// ── Tab button ──────────────────────────────────────────────────────────────
function TabBtn({ label, active, onClick, color = "#22d3ee" }) {
  return (
    <button onClick={onClick} style={{
      padding: ".45rem 1rem", fontSize: ".78rem", fontWeight: 600,
      border: "none", borderRadius: ".5rem", cursor: "pointer",
      background: active ? color : "transparent",
      color: active ? "#0f172a" : "#64748b",
      transition: "all .15s",
      fontFamily: "'Plus Jakarta Sans', sans-serif",
    }}>
      {label}
    </button>
  );
}

// ── Export pending invoices to Excel ────────────────────────────────────────
function exportToExcel(rows) {
  const data = rows.map(r => ({
    "Source":          r.source || "AMC",
    "Invoice No":      r.invoice_number || "",
    "Invoice Date":    r.invoice_date ? formatToDisplayDate(r.invoice_date) : "",
    "Client":          r.customer_name || "",
    "AMC":             r.amc_display || `AMC-${r.amc}` || "",
    "Period From":     r.period_start ? formatToDisplayDate(r.period_start) : "",
    "Period To":       r.period_end ? formatToDisplayDate(r.period_end) : "",
    "Description":     (r.description || "").trim(),
    "Grand Total (₹)": parseFloat(r.grand_total || 0).toFixed(2),
    "Received (₹)":    parseFloat(r.amount_received || 0).toFixed(2),
    "TDS IT (₹)":      parseFloat(r.tds_it  || 0).toFixed(2),
    "TDS GST (₹)":     parseFloat(r.tds_gst || 0).toFixed(2),
    "Pending (₹)":     parseFloat(r.pending_amount || 0).toFixed(2),
    "Status":          r.status || "",
    "Financial Year":  r.financial_year || "",
  }));
  const ws = XLSX.utils.json_to_sheet(data);
  
  // Set column widths for better readability
  ws['!cols'] = [
    { wch: 12 },  // Source
    { wch: 18 },  // Invoice No
    { wch: 20 },  // Invoice Date
    { wch: 20 },  // Client
    { wch: 10 },  // AMC
    { wch: 14 },  // Period From
    { wch: 14 },  // Period To
    { wch: 40 },  // Description
    { wch: 14 },  // Grand Total
    { wch: 14 },  // Received
    { wch: 12 },  // TDS IT
    { wch: 12 },  // TDS GST
    { wch: 14 },  // Pending
    { wch: 10 },  // Status
    { wch: 12 },  // Financial Year
  ];
  
  // Set number format for amount columns
  const amountColumns = ['I', 'J', 'K', 'L', 'M']; // Columns I-M for amounts
  for (let i = 2; i <= data.length + 1; i++) {
    amountColumns.forEach(col => {
      const cellRef = col + i;
      if (ws[cellRef]) {
        ws[cellRef].z = '#,##0.00'; // Number format
      }
    });
  }
  
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Pending Invoices");
  XLSX.writeFile(wb, `Pending_Invoices_${new Date().toISOString().split("T")[0]}.xlsx`);
}

export default function Dashboard({ role }) {
  const [stats,      setStats]      = useState(null);
  const [pending,    setPending]    = useState(null);
  const [ptg,        setPtg]        = useState(null);   // pending-to-generate
  const [workStats,  setWorkStats]  = useState(null);
  const [workPending,setWorkPending]= useState(null);
  const [dashTab,    setDashTab]    = useState("overview");
  const [pendingSource, setPendingSource] = useState("ALL");
  const [revenueSource, setRevenueSource] = useState("ALL");
  const [amcScan,    setAmcScan]    = useState(null);
  const [invScan,    setInvScan]    = useState(null);
  const [loadingAmc, setLoadingAmc] = useState(false);
  const [loadingInv, setLoadingInv] = useState(false);
  const [recentActions, setRecentActions] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [fyFilter,   setFyFilter]   = useState("ALL");
  const [pendingSearch, setPendingSearch] = useState("");   // search for pending invoices tab
  const [ptgSearch,     setPtgSearch]     = useState("");   // search for pending-to-generate tab
  const navigate = useNavigate();

  useEffect(() => {
    api.get("invoices/stats/").then(r => setStats(r.data)).catch(() => {});
    api.get("invoices/pending/").then(r => setPending(r.data)).catch(() => {});
    api.get("invoices/pending-to-generate/").then(r => setPtg(r.data)).catch(() => {});
    api.get("work-orders/stats/").then(r => setWorkStats(r.data)).catch(() => {});
    api.get("work-orders/pending/").then(r => setWorkPending(r.data)).catch(() => {});
    setLoadingRecent(true);
    api.get("audit-logs/recent/")
      .then(r => setRecentActions(r.data || []))
      .catch(() => setRecentActions([]))
      .finally(() => setLoadingRecent(false));
  }, []);

  const scanAMCs = async () => {
    setLoadingAmc(true);
    try { const r = await api.get("amc/upcoming/"); setAmcScan(r.data); }
    catch { setAmcScan([]); }
    setLoadingAmc(false);
  };

  const scanInvoices = async () => {
    setLoadingInv(true);
    try { const r = await api.get("invoices/upcoming/"); setInvScan(r.data); }
    catch { setInvScan([]); }
    setLoadingInv(false);
  };

  // FY list from stats
  const fyList = stats?.financial_years || [];
  const fyOptions = ["ALL", ...fyList.map(f => f.fy)];

  const workFyList = workStats?.financial_years || [];

  const combinedTotalInvoiced = (stats?.total_invoiced || 0) + (workStats?.total_amount || 0);
  const combinedTotalPending  = (stats?.total_pending || 0) + (workStats?.total_pending || 0);
  const combinedTotalReceived = (stats?.total_received || 0) + (workStats?.total_received || 0);

  const combinedPendingRows = [
    ...(pending || []).map(r => ({ ...r, source: "AMC", amc_display: `AMC-${r.amc}` })),
    ...(workPending || []).map(r => ({
      ...r,
      source: "Work Order",
      invoice_number: r.work_order_number,
      invoice_date: r.work_order_date,
      customer_name: r.customer_name,
      period_start: r.start_date,
      period_end: r.completion_date,
      amount_received: r.amount_received,
      pending_amount: r.pending_amount,
      financial_year: r.financial_year,
      status: r.status,
      description: r.description || "",
    }))
  ].filter(r => fyFilter === "ALL" || r.financial_year === fyFilter);

  const filteredPending = combinedPendingRows.filter(r => {
    if (pendingSource !== "ALL" && r.source !== pendingSource) return false;
    if (pendingSearch.trim()) {
      const q = pendingSearch.trim().toLowerCase();
      return (
        (r.invoice_number || "").toLowerCase().includes(q) ||
        (r.customer_name  || "").toLowerCase().includes(q) ||
        (r.description    || "").toLowerCase().includes(q) ||
        (r.amc_display    || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  const mergedFyMap = {};
  fyList.forEach(row => {
    mergedFyMap[row.fy] = {
      fy: row.fy,
      amc_value: row.amc_value || 0,
      amc_invoiced: row.invoiced || 0,
      amc_received: row.received || 0,
      amc_pending: row.pending || 0,
      wo_invoiced: 0,
      wo_received: 0,
      wo_pending: 0,
    };
  });
  workFyList.forEach(row => {
    if (!mergedFyMap[row.fy]) {
      mergedFyMap[row.fy] = {
        fy: row.fy,
        amc_value: 0,
        amc_invoiced: 0,
        amc_received: 0,
        amc_pending: 0,
        wo_invoiced: 0,
        wo_received: 0,
        wo_pending: 0,
      };
    }
    mergedFyMap[row.fy].wo_invoiced = row.invoiced || 0;
    mergedFyMap[row.fy].wo_received = row.received || 0;
    mergedFyMap[row.fy].wo_pending = row.pending || 0;
  });

  const mergedFyRows = Object.values(mergedFyMap)
    .map(row => ({
      ...row,
      total_invoiced: (row.amc_invoiced || 0) + (row.wo_invoiced || 0),
      total_received: (row.amc_received || 0) + (row.wo_received || 0),
      total_pending: (row.amc_pending || 0) + (row.wo_pending || 0),
    }))
    .sort((a, b) => b.fy.localeCompare(a.fy));

  const revenueRows = fyFilter === "ALL"
    ? mergedFyRows
    : mergedFyRows.filter(f => f.fy === fyFilter);

  return (
    <div>
      <PrivilegeNotice role={role} />

      <div style={{ marginBottom: "1.25rem" }}>
        <h2 style={{ fontSize:"1.4rem", fontWeight:700, color:"#f1f5f9" }}>System Core Hub</h2>
        <p style={{ fontSize:".875rem", color:"#64748b", marginTop:".2rem" }}>
          Financial metrics, pending collections, and upcoming scheduling workflows.
        </p>
      </div>

      {/* ── Top stats strip ── */}
      <div className="stats-grid" style={{ marginBottom:"1.5rem" }}>
        <div className="glow-card stat-card">
          <div><div className="label">Active Clients</div>
            <div className="value">{stats?.total_customers ?? "—"}</div></div>
          <div className="stat-icon cyan">
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
          </div>
        </div>
        <div className="glow-card stat-card">
          <div><div className="label">Live AMCs</div>
            <div className="value">{stats?.total_amc ?? "—"}</div></div>
          <div className="stat-icon purple">
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
            </svg>
          </div>
        </div>
        <div className="glow-card stat-card">
          <div><div className="label">AMC Invoiced</div>
            <div className="value emerald">{fmt(stats?.total_invoiced)}</div></div>
          <div className="stat-icon emerald">
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
            </svg>
          </div>
        </div>
        <div className="glow-card stat-card">
          <div><div className="label">AMC Pending</div>
            <div className="value" style={{color:"#f87171"}}>{fmt(stats?.total_pending)}</div></div>
          <div className="stat-icon" style={{background:"rgba(239,68,68,.1)",color:"#f87171",padding:".75rem",borderRadius:".75rem"}}>
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
        </div>
        <div className="glow-card stat-card">
          <div><div className="label">Work Order Invoiced</div>
            <div className="value emerald">{fmt(workStats?.total_amount)}</div></div>
          <div className="stat-icon emerald">
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
            </svg>
          </div>
        </div>
        <div className="glow-card stat-card">
          <div><div className="label">WO Pending</div>
            <div className="value" style={{color:"#f87171"}}>{fmt(workStats?.total_pending)}</div></div>
          <div className="stat-icon" style={{background:"rgba(239,68,68,.12)",color:"#f87171",padding:".75rem",borderRadius:".75rem"}}>
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
        </div>
      </div>

      {/* ── Dashboard tab bar ── */}
      <div style={{
        display:"flex", gap:".4rem", background:"#020617",
        border:"1px solid #1e293b", borderRadius:".75rem",
        padding:".35rem", marginBottom:"1.25rem", width:"fit-content"
      }}>
        <TabBtn label="Overview & Scanning"  active={dashTab==="overview"} onClick={()=>setDashTab("overview")} color="#22d3ee"/>
        <TabBtn label="Pending Invoices"      active={dashTab==="pending"}  onClick={()=>setDashTab("pending")}  color="#f87171"/>
        <TabBtn label="Pending to Generate"   active={dashTab==="ptg"}      onClick={()=>setDashTab("ptg")}      color="#c084fc"/>
        <TabBtn label="Revenue by FY"         active={dashTab==="revenue"}  onClick={()=>setDashTab("revenue")}  color="#34d399"/>
        <TabBtn label="Recent Actions"        active={dashTab==="activity"} onClick={()=>setDashTab("activity")} color="#f59e0b"/>
      </div>

      {/* ── FY filter (shared across pending + revenue tabs) ── */}
      {(dashTab === "pending" || dashTab === "revenue") && (
        <div style={{ marginBottom:"1rem", display:"flex", alignItems:"center", gap:".75rem" }}>
          <span style={{ fontSize:".75rem", color:"#64748b", fontWeight:600 }}>Financial Year:</span>
          <div style={{ display:"flex", gap:".4rem", flexWrap:"wrap" }}>
            {fyOptions.map(fy => (
              <button key={fy} onClick={()=>setFyFilter(fy)} style={{
                padding:".3rem .7rem", fontSize:".72rem", fontWeight:600,
                border:"1px solid", borderRadius:".4rem", cursor:"pointer",
                background: fyFilter===fy ? "#0f172a" : "transparent",
                color: fyFilter===fy ? "#22d3ee" : "#64748b",
                borderColor: fyFilter===fy ? "#22d3ee" : "#1e293b",
                fontFamily:"'Plus Jakarta Sans',sans-serif", transition:"all .15s"
              }}>{fy}</button>
            ))}
          </div>
        </div>
      )}

      {/* ══ TAB: OVERVIEW ══════════════════════════════════════════════════ */}
      {dashTab === "overview" && (
        <div className="scan-grid">
          {/* AMC Renewals */}
          <div className="glow-card scan-panel">
            <div className="scan-panel-header">
              <div>
                <div className="scan-panel-title"><span className="ping-purple"></span> Upcoming AMC Renewals</div>
                <div className="scan-panel-sub">Contracts expiring within the next 45 days.</div>
              </div>
              <button className="btn btn-purple btn-sm" onClick={scanAMCs} disabled={loadingAmc}>
                {loadingAmc?"Scanning…":"Scan Next 45 Days"}
              </button>
            </div>
            <div className="scan-output">
              {amcScan===null && <><svg width="24" height="24" fill="none" stroke="#334155" strokeWidth="1.5" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg><span>Hit button to fetch contract renewals</span></>}
              {amcScan!==null && amcScan.length===0 && <span>No contracts due within 45 days.</span>}
              {amcScan!==null && amcScan.length>0 && (
                <div className="scan-items-list">
                  {amcScan.map(a => (
                    <div key={a.id} className="scan-item">
                      <div>
                        <div style={{fontWeight:700,color:"#c084fc",fontSize:".75rem"}}>AMC-{a.id}</div>
                        <div style={{fontSize:".7rem",color:"#cbd5e1"}}>{a.customer_name}</div>
                        <div style={{fontSize:".65rem",color:"#64748b"}}>Expiring: {formatToDisplayDate(a.end_date)}</div>
                      </div>
                      <span className="badge badge-purple">{a.installment}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Upcoming Invoices */}
          <div className="glow-card scan-panel">
            <div className="scan-panel-header">
              <div>
                <div className="scan-panel-title"><span className="ping-emerald"></span> Queued Invoice Generations</div>
                <div className="scan-panel-sub">Overdue + billing cycles due within 45 days, sorted by urgency.</div>
              </div>
              <button className="btn btn-emerald btn-sm" onClick={scanInvoices} disabled={loadingInv}>
                {loadingInv?"Scanning…":"Scan"}
              </button>
            </div>
            <div className="scan-output">
              {invScan===null && <><svg width="24" height="24" fill="none" stroke="#334155" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg><span>Hit button to fetch pending billing cycles</span></>}
              {invScan!==null && invScan.length===0 && <span>No billing cycles due or overdue.</span>}
              {invScan!==null && invScan.length>0 && (
                <div className="scan-items-list">
                  {invScan.map((item,i) => (
                    <div key={i} className="scan-item" style={{
                      borderLeft: item.is_overdue ? "3px solid #f87171" : "3px solid #34d399",
                      paddingLeft:".6rem", gap:".5rem",
                    }}>
                      <div style={{flex:1, minWidth:0}}>
                        <div style={{display:"flex", alignItems:"center", gap:".35rem", flexWrap:"wrap", marginBottom:".2rem"}}>
                          {item.is_overdue
                            ? <span className="badge badge-red" style={{fontSize:".65rem"}}>⚠ OVERDUE {item.days_overdue}d</span>
                            : <span className="badge badge-emerald" style={{fontSize:".65rem"}}>Due {formatToDisplayDate(item.due_date)}</span>
                          }
                          <span className="badge badge-purple" style={{fontSize:".62rem"}}>{item.installment}</span>
                          <span className="badge badge-slate" style={{fontSize:".62rem"}}>{item.payment_mode}</span>
                        </div>
                        <div style={{fontSize:".75rem",color:"#f1f5f9",fontWeight:600}}>{item.customer_name}</div>
                        <div style={{fontSize:".65rem",color:"#64748b", marginTop:".1rem"}}>
                          AMC-{item.amc_id}&nbsp;·&nbsp;
                          {formatToDisplayDate(item.period_start)} → {formatToDisplayDate(item.period_end)}
                        </div>
                      </div>
                      <button className="btn btn-emerald btn-xs" style={{flexShrink:0}}
                        onClick={()=>{ try{ sessionStorage.setItem('ihrd_pref_invoice', JSON.stringify({ amc: item.amc_id, period_start: item.period_start })); } catch(_) {} navigate('/invoices'); }}>
                        Generate →
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ TAB: PENDING INVOICES ══════════════════════════════════════════ */}
      {dashTab === "pending" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:"1rem", marginBottom:"1rem" }}>
            <div>
              <span style={{ fontSize:".875rem", fontWeight:600, color:"#f1f5f9" }}>
                Pending Collections
              </span>
              <span style={{ marginLeft:".75rem", fontSize:".75rem", color:"#64748b" }}>
                {filteredPending.length} record{filteredPending.length!==1?"s":""} · Total pending: <span style={{color:"#f87171",fontWeight:700}}>{fmt(filteredPending.reduce((s,r)=>s+r.pending_amount,0))}</span>
              </span>
            </div>
            <button className="btn btn-emerald btn-sm" onClick={()=>exportToExcel(filteredPending)} disabled={filteredPending.length===0}>
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
              </svg>
              Download Excel
            </button>
          </div>

          <div style={{ display:"flex", gap:".5rem", flexWrap:"wrap", marginBottom:"1rem", alignItems:"center" }}>
            {/* Search box */}
            <div style={{ position:"relative", flex:"1", minWidth:"200px", maxWidth:"360px" }}>
              <svg width="14" height="14" fill="none" stroke="#475569" strokeWidth="2" viewBox="0 0 24 24"
                style={{ position:"absolute", left:".65rem", top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}>
                <circle cx="11" cy="11" r="8"/><path strokeLinecap="round" d="M21 21l-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder="Search invoice no, client, description…"
                value={pendingSearch}
                onChange={e => setPendingSearch(e.target.value)}
                style={{
                  width:"100%", paddingLeft:"2rem", paddingRight:pendingSearch?".5rem":".75rem",
                  paddingTop:".35rem", paddingBottom:".35rem",
                  background:"#0f172a", border:"1px solid #1e293b",
                  borderRadius:".5rem", color:"#f1f5f9", fontSize:".78rem",
                  fontFamily:"'Plus Jakarta Sans',sans-serif", outline:"none", boxSizing:"border-box",
                }}
              />
              {pendingSearch && (
                <button onClick={() => setPendingSearch("")}
                  style={{ position:"absolute", right:".5rem", top:"50%", transform:"translateY(-50%)",
                    background:"none", border:"none", color:"#475569", cursor:"pointer", fontSize:".9rem", lineHeight:1 }}>
                  ×
                </button>
              )}
            </div>
            {/* Source filter */}
            {['ALL','AMC','Work Order'].map(source => (
              <button key={source} onClick={() => setPendingSource(source)} style={{
                padding:'.35rem .8rem', fontSize:'.75rem', fontWeight:600,
                border:'1px solid', borderRadius:'.5rem', cursor:'pointer',
                background: pendingSource===source ? '#0f172a' : 'transparent',
                color: pendingSource===source ? '#22d3ee' : '#64748b',
                borderColor: pendingSource===source ? '#22d3ee' : '#1e293b',
              }}>
                {source}
              </button>
            ))}
          </div>

          <div className="table-wrap">
            <table className="data-table" style={{tableLayout:"auto", width:"100%", minWidth:"900px"}}>
              <colgroup>
                <col style={{width:"1%", whiteSpace:"nowrap"}} />{/* Source — shrink to badge */}
                <col style={{width:"1%", whiteSpace:"nowrap"}} />{/* Invoice No — shrink to content */}
                <col style={{width:"1%", whiteSpace:"nowrap"}} />{/* Invoice Date */}
                <col style={{width:"15%", minWidth:"110px"}}   />{/* Client — proportional, no word split */}
                <col style={{width:"1%", whiteSpace:"nowrap"}} />{/* Period — shrink to date width */}
                <col                                           />{/* Description — flex remaining */}
                <col style={{width:"1%", whiteSpace:"nowrap"}} />{/* Grand Total */}
                <col style={{width:"1%", whiteSpace:"nowrap"}} />{/* Received */}
                <col style={{width:"1%", whiteSpace:"nowrap"}} />{/* Pending */}
                <col style={{width:"1%", whiteSpace:"nowrap"}} />{/* Status */}
                <col style={{width:"1%", whiteSpace:"nowrap"}} />{/* FY */}
              </colgroup>
              <thead>
                <tr>
                  <th style={{whiteSpace:"nowrap"}}>Source</th>
                  <th style={{whiteSpace:"nowrap"}}>Invoice No</th>
                  <th style={{whiteSpace:"nowrap"}}>Invoice Date</th>
                  <th>Client</th>
                  <th style={{whiteSpace:"nowrap", textAlign:"center"}}>Period</th>
                  <th>Description</th>
                  <th style={{whiteSpace:"nowrap", textAlign:"right"}}>Grand Total</th>
                  <th style={{whiteSpace:"nowrap", textAlign:"right"}}>Received</th>
                  <th style={{whiteSpace:"nowrap", textAlign:"right"}}>Pending</th>
                  <th style={{whiteSpace:"nowrap"}}>Status</th>
                  <th style={{whiteSpace:"nowrap"}}>FY</th>
                </tr>
              </thead>
              <tbody>
                {filteredPending.length===0 && (
                  <tr><td colSpan="11">
                    <div className="empty-state">
                      {pending===null || workPending===null ? "Loading…" : "No pending invoices 🎉"}
                    </div>
                  </td></tr>
                )}
                {filteredPending.map(r => (
                  <tr key={`${r.source}-${r.id}`}>
                    <td style={{whiteSpace:"nowrap", verticalAlign:"middle"}}>
                      <span className={`badge ${r.source==="AMC"?"badge-cyan":"badge-purple"}`}>
                        {r.source}
                      </span>
                    </td>
                    <td style={{fontWeight:700, color:"#34d399", whiteSpace:"nowrap", verticalAlign:"middle"}}>
                      {r.invoice_number}
                    </td>
                    <td className="date-cell" style={{color:"#94a3b8", fontSize:".8rem", verticalAlign:"middle"}}>
                      {r.invoice_date ? formatToDisplayDate(r.invoice_date) : "—"}
                    </td>
                    <td style={{
                      color:"#f1f5f9",
                      wordBreak:"keep-all",
                      overflowWrap:"anywhere",
                      whiteSpace:"normal",
                      verticalAlign:"middle",
                    }}>
                      {r.customer_name}
                    </td>
                    <td style={{verticalAlign:"middle", padding:".75rem .6rem", textAlign:"center"}}>
                      <div style={{
                        display:"flex", flexDirection:"column",
                        alignItems:"center", gap:"1px",
                        color:"#94a3b8", fontSize:".78rem",
                      }}>
                        <span style={{whiteSpace:"nowrap"}}>{formatToDisplayDate(r.period_start) || "—"}</span>
                        <span style={{color:"#475569", fontSize:".7rem", lineHeight:1}}>↓</span>
                        <span style={{whiteSpace:"nowrap"}}>{formatToDisplayDate(r.period_end) || "—"}</span>
                      </div>
                    </td>
                    <td style={{color:"#cbd5e1", fontSize:".8rem", whiteSpace:"normal", wordBreak:"break-word", overflowWrap:"break-word", verticalAlign:"middle"}}>
                      {r.description ? r.description.substring(0, 80) + (r.description.length > 80 ? "…" : "") : "—"}
                    </td>
                    <td style={{color:"#34d399", fontWeight:600, whiteSpace:"nowrap", textAlign:"right", verticalAlign:"middle"}}>{fmt(r.grand_total)}</td>
                    <td style={{color:"#22d3ee", whiteSpace:"nowrap", textAlign:"right", verticalAlign:"middle"}}>{r.amount_received>0?fmt(r.amount_received):"—"}</td>
                    <td style={{color:"#f87171", fontWeight:700, whiteSpace:"nowrap", textAlign:"right", verticalAlign:"middle"}}>{fmt(r.pending_amount)}</td>
                    <td style={{whiteSpace:"nowrap", verticalAlign:"middle"}}>
                      <span className={`badge ${r.status==="Partial"?"badge-purple":"badge-red"}`}>{r.status}</span>
                    </td>
                    <td style={{whiteSpace:"nowrap", verticalAlign:"middle"}}><span className="badge badge-slate">{r.financial_year}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══ TAB: PENDING TO GENERATE ══════════════════════════════════ */}
      {dashTab === "ptg" && (
        <div>
          {/* PTG search box */}
          <div style={{ position:"relative", marginBottom:"1rem", maxWidth:"400px" }}>
            <svg width="14" height="14" fill="none" stroke="#475569" strokeWidth="2" viewBox="0 0 24 24"
              style={{ position:"absolute", left:".65rem", top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}>
              <circle cx="11" cy="11" r="8"/><path strokeLinecap="round" d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search client or AMC no…"
              value={ptgSearch}
              onChange={e => setPtgSearch(e.target.value)}
              style={{
                width:"100%", paddingLeft:"2rem", paddingRight:ptgSearch?".5rem":".75rem",
                paddingTop:".4rem", paddingBottom:".4rem",
                background:"#0f172a", border:"1px solid #1e293b",
                borderRadius:".5rem", color:"#f1f5f9", fontSize:".78rem",
                fontFamily:"'Plus Jakarta Sans',sans-serif", outline:"none", boxSizing:"border-box",
              }}
            />
            {ptgSearch && (
              <button onClick={() => setPtgSearch("")}
                style={{ position:"absolute", right:".5rem", top:"50%", transform:"translateY(-50%)",
                  background:"none", border:"none", color:"#475569", cursor:"pointer", fontSize:".9rem", lineHeight:1 }}>
                ×
              </button>
            )}
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1rem" }}>
            <div>
              <span style={{ fontSize:".875rem", fontWeight:600, color:"#f1f5f9" }}>
                Invoices Pending to be Generated
              </span>
              <span style={{ marginLeft:".75rem", fontSize:".75rem", color:"#64748b" }}>
                {(ptg||[]).length} AMC contract{(ptg||[]).length!==1?"s":""} with uninvoiced periods ·&nbsp;
                {(ptg||[]).reduce((s,r)=>s+r.missing_count,0)} billing slot{(ptg||[]).reduce((s,r)=>s+r.missing_count,0)!==1?"s":""} pending
              </span>
            </div>
            <button className="btn btn-purple btn-sm"
              onClick={() => {
                if (!ptg || ptg.length===0) return;
                const rows = [];
                ptg.forEach(c => c.missing_periods.forEach(p => rows.push({
                  "AMC": c.amc_display, "Client": c.customer_name,
                  "Installment": c.installment,
                  "Period From": p.period_start ? formatToDisplayDate(p.period_start) : "",
                  "Period To": p.period_end ? formatToDisplayDate(p.period_end) : "",
                  "AMC Value (₹)": c.value,
                })));
                // inline xlsx export
                import("xlsx").then(XLSX => {
                  const ws = XLSX.utils.json_to_sheet(rows);
                  const wb = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(wb, ws, "Pending to Generate");
                  XLSX.writeFile(wb, `Pending_to_Generate_${new Date().toISOString().split("T")[0]}.xlsx`);
                });
              }}>
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
              </svg>
              Download Excel
            </button>
          </div>

          {ptg === null && <div className="empty-state">Loading…</div>}
          {ptg !== null && ptg.length === 0 && (
            <div className="empty-state" style={{ padding:"3rem", textAlign:"center" }}>
              <div style={{ fontSize:"2rem", marginBottom:".5rem" }}>✅</div>
              All AMC contracts are fully invoiced. Nothing pending.
            </div>
          )}

          {ptg !== null && ptg.length > 0 && (() => {
            const filteredPtg = ptgSearch.trim()
              ? ptg.filter(c => {
                  const q = ptgSearch.trim().toLowerCase();
                  return (
                    (c.customer_name || "").toLowerCase().includes(q) ||
                    (c.amc_display   || "").toLowerCase().includes(q)
                  );
                })
              : ptg;
            return (
            <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
              {filteredPtg.length === 0 && (
                <div className="empty-state">No results for "{ptgSearch}"</div>
              )}
              {filteredPtg.map(contract => (
                <div key={contract.amc_id} className="glow-card" style={{ overflow:"hidden" }}>
                  {/* Contract header */}
                  <div style={{
                    background:"#020617", padding:".75rem 1rem",
                    borderBottom:"1px solid #1e293b",
                    display:"flex", justifyContent:"space-between", alignItems:"center",
                    flexWrap:"wrap", gap:".5rem"
                  }}>
                    <div style={{ display:"flex", alignItems:"center", gap:".75rem" }}>
                      <span style={{ fontWeight:700, color:"#c084fc", fontSize:".85rem" }}>
                        {contract.amc_display}
                      </span>
                      <span style={{ color:"#f1f5f9", fontSize:".875rem" }}>{contract.customer_name}</span>
                      <span className="badge badge-purple">{contract.installment}</span>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:"1rem", fontSize:".75rem" }}>
                      <span style={{ color:"64748b" }}>
                        {formatToDisplayDate(contract.start_date)} → {formatToDisplayDate(contract.end_date)}
                      </span>
                      <span style={{ color:"#34d399", fontWeight:600 }}>
                        ₹{Math.round(contract.value).toLocaleString("en-IN")}
                      </span>
                      <span style={{
                        background:"rgba(239,68,68,.12)", border:"1px solid rgba(239,68,68,.25)",
                        color:"#f87171", borderRadius:".4rem", padding:".15rem .5rem",
                        fontWeight:700, fontSize:".7rem"
                      }}>
                        {contract.missing_count} slot{contract.missing_count!==1?"s":""} uninvoiced
                      </span>
                    </div>
                  </div>

                  {/* Missing periods table */}
                  <table className="data-table" style={{ margin:0 }}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Period From</th>
                        <th>Period To</th>
                        <th>Estimated Amount</th>
                        <th className="th-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contract.missing_periods.map((p, i) => {
                        const splits = {Full:1, "Half-Yearly":2, Quarterly:4, Monthly:12};
                        const est = Math.round(contract.value / (splits[contract.installment]||1));
                        return (
                          <tr key={i}>
                            <td style={{ color:"#64748b", fontSize:".75rem" }}>{i+1}</td>
                            <td className="date-cell" style={{ color:"#cbd5e1" }}>
                              {formatToDisplayDate(p.period_start)}
                              <div style={{ fontSize:'.72rem', color:'#94a3b8' }}>
                                {computePeriodLabel(p.period_start, contract.start_date, contract.installment)}
                              </div>
                            </td>
                            <td className="date-cell" style={{ color:"#cbd5e1" }}>{formatToDisplayDate(p.period_end)}</td>
                            <td style={{ color:"#34d399", fontWeight:600 }}>
                              ~₹{est.toLocaleString("en-IN")}
                            </td>
                            <td className="td-right">
                              <button className="btn btn-purple btn-xs"
                                onClick={() => {
                                  try { sessionStorage.setItem('ihrd_pref_invoice', JSON.stringify({ amc: contract.amc_id, period_start: p.period_start })); } catch(_) {}
                                  navigate('/invoices');
                                }}>
                                Generate →
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
            );
          })()}
        </div>
      )}

      {/* ══ TAB: REVENUE BY FINANCIAL YEAR ════════════════════════════════ */}
      {dashTab === "revenue" && (
        <div>
          <div style={{ marginBottom:"1rem", display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:"1rem" }}>
            <div>
              <span style={{ fontSize:".875rem", fontWeight:600, color:"#f1f5f9" }}>Revenue Summary</span>
              <span style={{ marginLeft:".75rem", fontSize:".75rem", color:"#64748b" }}>
                Auto-grouped by Financial Year (Apr–Mar)
              </span>
            </div>
            <div style={{ display:"flex", gap:".5rem", flexWrap:"wrap" }}>
              {['ALL','AMC','Work Orders'].map(source => (
                <button key={source} onClick={() => setRevenueSource(source)} style={{
                  padding:'.35rem .8rem', fontSize:'.75rem', fontWeight:600,
                  border:'1px solid', borderRadius:'.5rem', cursor:'pointer',
                  background: revenueSource===source ? '#0f172a' : 'transparent',
                  color: revenueSource===source ? '#22d3ee' : '#64748b',
                  borderColor: revenueSource===source ? '#22d3ee' : '#1e293b',
                }}>
                  {source}
                </button>
              ))}
            </div>
          </div>

          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Financial Year</th>
                  {revenueSource !== 'Work Orders' && <th>Total AMC Value</th>}
                  <th>{revenueSource === 'ALL' ? 'Total Invoiced' : revenueSource === 'AMC' ? 'AMC Invoiced' : 'WO Invoiced'}</th>
                  <th>Amount Received</th>
                  <th>Pending to Collect</th>
                  <th>Collection %</th>
                </tr>
              </thead>
              <tbody>
                {revenueRows.length===0 && (
                  <tr><td colSpan={revenueSource !== 'Work Orders' ? 6 : 5}><div className="empty-state">No revenue data available.</div></td></tr>
                )}
                {revenueRows.map(row => {
                  const invoiced = revenueSource === 'ALL' ? row.total_invoiced : revenueSource === 'AMC' ? row.amc_invoiced : row.wo_invoiced;
                  const received = revenueSource === 'ALL' ? row.total_received : revenueSource === 'AMC' ? row.amc_received : row.wo_received;
                  const pending = revenueSource === 'ALL' ? row.total_pending : revenueSource === 'AMC' ? row.amc_pending : row.wo_pending;
                  const pct = invoiced > 0 ? Math.round((received / invoiced) * 100) : 0;
                  return (
                    <tr key={row.fy}>
                      <td style={{fontWeight:700,color:"#22d3ee"}}>
                        <span className="badge badge-cyan">{row.fy}</span>
                      </td>
                      {revenueSource !== 'Work Orders' && (
                        <td style={{color:"#c084fc",fontWeight:600}}>{fmt(row.amc_value)}</td>
                      )}
                      <td style={{color:"#34d399",fontWeight:600}}>{fmt(invoiced)}</td>
                      <td style={{color:"#22d3ee",fontWeight:600}}>{fmt(received)}</td>
                      <td style={{color:pending>0?"#f87171":"#34d399",fontWeight:700}}>{fmt(pending)}</td>
                      <td>
                        <div style={{ display:"flex", alignItems:"center", gap:".5rem" }}>
                          <div style={{
                            flex:1, height:6, background:"#1e293b", borderRadius:3, overflow:"hidden"
                          }}>
                            <div style={{
                              width:`${pct}%`, height:"100%", borderRadius:3,
                              background: pct>=100?"#10b981":pct>=50?"#22d3ee":"#f59e0b",
                              transition:"width .3s"
                            }}/>
                          </div>
                          <span style={{
                            fontSize:".75rem",fontWeight:700,
                            color: pct>=100?"#34d399":pct>=50?"#22d3ee":"#fbbf24",
                            minWidth:35
                          }}>{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Totals row */}
              {revenueRows.length>0 && (
                <tfoot>
                  <tr style={{background:"#020617",borderTop:"2px solid #1e293b"}}>
                    <td style={{fontWeight:700,color:"#f1f5f9",padding:"1rem"}}>TOTAL</td>
                    {revenueSource !== 'Work Orders' && (
                      <td style={{color:"#c084fc",fontWeight:700,padding:"1rem"}}>{fmt(revenueRows.reduce((s,r)=>s+r.amc_value,0))}</td>
                    )}
                    <td style={{color:"#34d399",fontWeight:700,padding:"1rem"}}>{fmt(revenueRows.reduce((s,row)=>s + (revenueSource === 'ALL' ? row.total_invoiced : revenueSource === 'AMC' ? row.amc_invoiced : row.wo_invoiced), 0))}</td>
                    <td style={{color:"#22d3ee",fontWeight:700,padding:"1rem"}}>{fmt(revenueRows.reduce((s,row)=>s + (revenueSource === 'ALL' ? row.total_received : revenueSource === 'AMC' ? row.amc_received : row.wo_received), 0))}</td>
                    <td style={{color:"#f87171",fontWeight:700,padding:"1rem"}}>{fmt(revenueRows.reduce((s,row)=>s + (revenueSource === 'ALL' ? row.total_pending : revenueSource === 'AMC' ? row.amc_pending : row.wo_pending), 0))}</td>
                    <td style={{padding:"1rem"}}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* ══ TAB: RECENT ACTIONS ══════════════════════════════════════════ */}
      {dashTab === "activity" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:"1rem", marginBottom:"1rem" }}>
            <div>
              <span style={{ fontSize:".875rem", fontWeight:600, color:"#f1f5f9" }}>
                Recent Activity
              </span>
              <span style={{ marginLeft:".75rem", fontSize:".75rem", color:"#64748b" }}>
                Your latest 5 actions from the audit trail
              </span>
            </div>
          </div>

          <div className="glow-card" style={{ padding:"1rem" }}>
            {loadingRecent && <div className="empty-state">Loading recent activity…</div>}
            {!loadingRecent && recentActions.length === 0 && (
              <div className="empty-state">No recent actions found for your account.</div>
            )}
            {!loadingRecent && recentActions.length > 0 && (
              <div style={{ display:"flex", flexDirection:"column", gap:".75rem" }}>
                {recentActions.map(action => (
                  <div key={action.id} style={{ border:"1px solid #1e293b", borderRadius:".75rem", padding:"0.85rem 1rem", background:"rgba(2,6,23,.6)" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:"1rem", flexWrap:"wrap" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:".6rem" }}>
                        <span className={`badge ${action.action === 'CREATE' ? 'badge-emerald' : action.action === 'UPDATE' ? 'badge-cyan' : 'badge-red'}`}>
                          {action.action}
                        </span>
                        <span style={{ color:"#f8fafc", fontWeight:700 }}>{action.model_name}</span>
                      </div>
                      <span style={{ fontSize:".75rem", color:"#64748b" }}>
                        {action.timestamp ? new Date(action.timestamp).toLocaleString() : "—"}
                      </span>
                    </div>
                    <div style={{ marginTop:".4rem", color:"#cbd5e1", fontSize:".8rem" }}>
                      {action.object_display || "No details available"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
